/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Column } from 'react-table';
import { Chevron } from '../../Atoms/Chevron/Chevron';
import { If } from '../../Atoms/Conditionals';
import { useViewport } from '../../hooks';
import { CoreTable } from './CoreTable';
import { TableStyles } from './styles';

export type ViewMoreConfig = {
  itemsToShow: number;
  itemsToExpand: number;
  viewMoreText?: string;
  viewLessText?: string;
};

interface TableProps {
  data: any[];
  columns: Column<any>[];
  tableId: string;
  sorted?: boolean;
  viewMoreToggle?: boolean;
  viewMore?: ViewMoreConfig;
  borderRadius?: number;
  padding?: number;
  columnBorder?: boolean;
  chevronThickness?: number;
  smallWidth?: boolean;
  stickyFirstColumn?: boolean;
}

const Table: React.FC<TableProps> = ({
  data,
  columns,
  tableId,
  sorted = false,
  viewMoreToggle = true,
  viewMore = {
    itemsToShow: 5,
    itemsToExpand: 4,
    viewMoreText: 'View more',
    viewLessText: 'View less',
  },
  borderRadius = 12,
  padding = 16,
  columnBorder = false,
  chevronThickness = 10,
  smallWidth = false,
  stickyFirstColumn = false,
}): JSX.Element => {
  const [rowsToShow, setRowsToShow] = useState(
    viewMoreToggle && viewMore.itemsToShow <= data.length
      ? [...data].slice(0, viewMore.itemsToShow)
      : [...data]
  );

  const [viewMoreLess, setViewMoreLess] = useState<'more' | 'less'>('more');
  const { width } = useViewport();
  const [tableWidth, setTableWidth] = useState(100);
  const [refTable, inViewTable] = useInView({
    triggerOnce: true,
  });

  const addRowsToRowsToShow = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (rowsToShow.length + viewMore.itemsToExpand <= data.length) {
      setRowsToShow(
        [...data].slice(0, rowsToShow.length + viewMore.itemsToExpand)
      );
    } else {
      setRowsToShow([...data]);
    }
  };

  const removeRowsToRowsToShow = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (rowsToShow.length - viewMore.itemsToExpand >= viewMore.itemsToShow) {
      setRowsToShow(
        [...data].slice(0, rowsToShow.length - viewMore.itemsToExpand)
      );
    } else {
      setRowsToShow(
        [...data].slice(
          0,
          viewMore.itemsToShow <= data.length
            ? viewMore.itemsToShow
            : data.length
        )
      );
    }
  };

  useEffect(() => {
    if (rowsToShow.length === data.length) {
      setViewMoreLess('less');
    } else if (viewMore && rowsToShow.length === viewMore.itemsToShow) {
      setViewMoreLess('more');
    }
  }, [data.length, rowsToShow, viewMore]);

  useEffect(() => {
    const tableEle = document.getElementById(tableId) as HTMLElement;
    setTableWidth(tableEle.offsetWidth);
  }, [tableId, width, inViewTable]);
  /*
   * Added inViewTable as a dependancy as in mobile view the table is
   * inside an accordian component which only renders the children on open state.
   *  Hence the tableEle on page load will not be rendered and
   * hanve a width of 0 breaking the styles. Hence we use this inViewTable hook which
   *  will trigger the useEffect hook to apply the required style
   */
  useEffect(() => {
    setRowsToShow(
      viewMoreToggle && viewMore.itemsToShow <= data.length
        ? [...data].slice(0, viewMore.itemsToShow)
        : [...data]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  return (
    <TableStyles
      ref={refTable}
      isViewMoreActive={viewMoreToggle}
      borderRadius={borderRadius}
      padding={padding}
      columnBorder={columnBorder}
      smallWidth={smallWidth}
      stickyFirstColumn={stickyFirstColumn}
    >
      <CoreTable
        data={rowsToShow}
        columns={columns}
        sorted={sorted}
        id={tableId}
      />
      <If condition={viewMoreToggle}>
        <button
          data-testid="viewMore"
          style={{ width: tableWidth }}
          className={`viewMore w-full font-semibold text-base text-blue-350 flex items-center justify-center outline-none focus:outline-none`}
          onClick={(event: React.MouseEvent | React.TouchEvent) => {
            return viewMoreLess === 'more'
              ? addRowsToRowsToShow(event)
              : removeRowsToRowsToShow(event);
          }}
        >
          {viewMoreLess === 'more'
            ? viewMore.viewMoreText
              ? viewMore.viewMoreText
              : 'View more'
            : viewMore.viewLessText
            ? viewMore.viewLessText
            : 'View less'}
          <Chevron
            direction={viewMoreLess === 'more' ? 'down' : 'up'}
            className="mt-1 ml-5"
            thickness={chevronThickness}
          ></Chevron>
        </button>
      </If>
    </TableStyles>
  );
};

export { Table };
